import React from "react";
import { useHistory, useParams } from "react-router-dom";
import OTLayout from "./OTLayout";
import { OT_BASE, C } from "./constants";
import { WORKFLOWS, getWorkflow } from "./workflows";
import {
  OtPage,
  SectionCard,
  CategoryBadge,
  OtLinkButton,
  OtCardButton,
  Box,
  Grid,
  Typography,
  Stack,
} from "./ui";
import SvgIcon from "../ipd-billing/SvgIcon";

export default function OTWorkflowDetail() {
  const history = useHistory();
  const { slug } = useParams();
  const wf = getWorkflow(slug);

  if (!wf) {
    return (
      <OTLayout activeNav="workflows">
        <OtPage>
          <Typography sx={{ color: C.textSub }}>Workflow not found.</Typography>
          <OtLinkButton onClick={() => history.push(`${OT_BASE}/workflows`)}>Back to workflows</OtLinkButton>
        </OtPage>
      </OTLayout>
    );
  }

  const idx = WORKFLOWS.findIndex((w) => w.slug === wf.slug);
  const prev = WORKFLOWS[idx - 1];
  const next = WORKFLOWS[idx + 1];
  const roleCount = new Set(wf.steps.map((s) => s.actor)).size;

  return (
    <OTLayout activeNav="workflows">
      <OtPage>
        <OtLinkButton
          onClick={() => history.push(`${OT_BASE}/workflows`)}
          startIcon={<SvgIcon name="back" size={14} color={C.textSub} />}
          sx={{ color: C.textSub, display: "inline-flex", alignItems: "center", gap: 0.75 }}
        >
          All workflows
        </OtLinkButton>

        <SectionCard sx={{ mt: 2, p: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2.5}>
            <Box sx={{ maxWidth: 720 }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ fontSize: 11 }}>
                <Typography sx={{ fontFamily: "monospace", color: C.textSub, fontSize: 11 }}>{wf.code}</Typography>
                <Typography sx={{ color: C.textSub, fontSize: 11 }}>§ {wf.number}</Typography>
                <CategoryBadge category={wf.category} />
              </Stack>
              <Typography component="h1" sx={{ mt: 1.5, fontSize: 28, fontWeight: 700, color: C.text }}>{wf.title}</Typography>
              <Typography sx={{ mt: 1.25, fontSize: 14, lineHeight: 1.5, color: C.textSub }}>{wf.summary}</Typography>
            </Box>
            <Grid container spacing={1.25} sx={{ minWidth: 240, maxWidth: 320 }}>
              {[
                { icon: "clock", value: wf.steps.length, label: "Stages" },
                { icon: "building", value: roleCount, label: "Roles" },
                { icon: "check", value: "v2.1", label: "SOP" },
              ].map((stat) => (
                <Grid item xs={4} key={stat.label}>
                  <Box sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", p: 1.5, textAlign: "center" }}>
                    <SvgIcon name={stat.icon} size={16} color={C.primary} />
                    <Typography sx={{ mt: 0.75, fontSize: 18, fontWeight: 700, color: C.text }}>{stat.value}</Typography>
                    <Typography sx={{ fontSize: 11, color: C.textSub }}>{stat.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </SectionCard>

        <SectionCard sx={{ mt: 2.5, p: 3 }}>
          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>Process flow</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: C.textSub }}>Sequential stages, owners and intent.</Typography>
          <Box component="ol" sx={{ mt: 2.5, p: 0, m: 0, listStyle: "none" }}>
            {wf.steps.map((step, i) => (
              <Box
                component="li"
                key={step.title}
                sx={{
                  display: "flex",
                  gap: 1.75,
                  pb: i < wf.steps.length - 1 ? 2.5 : 0,
                  position: "relative",
                }}
              >
                {i < wf.steps.length - 1 && (
                  <Box
                    aria-hidden
                    sx={{
                      position: "absolute",
                      left: 15,
                      top: 32,
                      bottom: 0,
                      width: 1,
                      bgcolor: C.border,
                    }}
                  />
                )}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: C.primary,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </Box>
                <PaperStep step={step} />
              </Box>
            ))}
          </Box>
        </SectionCard>

        <Grid container spacing={1.5} sx={{ mt: 2.5 }} component="nav">
          <Grid item xs={12} sm={6}>
            {prev ? (
              <OtCardButton onClick={() => history.push(`${OT_BASE}/workflows/${prev.slug}`)} sx={{ flexDirection: "row", alignItems: "center" }}>
                <Box>
                  <Typography sx={{ fontSize: 11, color: C.textSub }}>Previous</Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, fontWeight: 600 }}>{prev.title}</Typography>
                </Box>
                <SvgIcon name="back" size={14} color={C.textSub} />
              </OtCardButton>
            ) : null}
          </Grid>
          <Grid item xs={12} sm={6}>
            {next ? (
              <OtCardButton onClick={() => history.push(`${OT_BASE}/workflows/${next.slug}`)} sx={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <Box sx={{ ml: "auto", textAlign: "right" }}>
                  <Typography sx={{ fontSize: 11, color: C.textSub }}>Next</Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, fontWeight: 600 }}>{next.title}</Typography>
                </Box>
              </OtCardButton>
            ) : null}
          </Grid>
        </Grid>
      </OtPage>
    </OTLayout>
  );
}

function PaperStep({ step }) {
  return (
    <Box sx={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: "12px", p: 1.75, bgcolor: "#fff" }}>
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{step.title}</Typography>
        <Typography sx={{ fontSize: 12, color: C.textSub }}>{step.actor}</Typography>
      </Stack>
      <Typography sx={{ mt: 0.75, fontSize: 13, color: C.textSub }}>{step.description}</Typography>
    </Box>
  );
}
